/**
 * The planning day (V19 handoff, 2026-10-08): one day on an hour scale, a track per person plus
 * the open row. Each shift is its own container, the shift above and one activity lane below
 * with its tasks and its lunch (planned, or suggested past 5.5 hours). The scale opens on the
 * day's working window and keeps all 24 hours a scroll away; names and the ruler stay put.
 *
 * Dragging a card to another track reassigns it; dragging its edges changes the start or the
 * end. Both go through the same write, refusal and recovery as the week grid. The panel and the
 * drawer are the keyboard and touch way to do the same (Endre, the edge between two shifts).
 *
 * On the activity lane a task or the lunch moves by dragging it and changes length by its edges,
 * and dragging over an empty stretch draws a new activity (review, completion list). The ghost
 * shows the time and any overlap while dragging and stays inside the shift; the release saves
 * nothing, it opens the change in the panel to be saved there, as the prototype does
 * ("Endringen må lagres"). Escape or a cancelled pointer leaves everything as it was.
 */
import type { ApiShift, ApiUser } from '@/api/data';
import { T } from '@/i18n';
import { oslo, osloISO } from '@/rules';
import { Tip } from '@/ui';
import { fmtDs, fullName, initials, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { addDays } from '@timefly/core';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
// the plain class names (dlane, dact, lunch, sugg) are hooks for the gestures and the e2e specs; the look is the module's
import ds from './DayView.module.css';
import { freeSlot } from './TaskEditor';
import { activityIcon } from './activityIcon';
import { plannedLunch, suggestLunch } from './lunch';
import { type Clash, clashesOf } from './overlapFix';

const MIN = 60_000;
/** Popover.Close, Dialog.Close, or nothing to close (the phone list) */
type CloseLike = (p: { asChild?: boolean; children: ReactNode }) => ReactNode;
const Keep: CloseLike = ({ children }) => children;
/** where a lunch, or a task without a time, opens in the editor: its tasks and break section */
const PAUSE = { section: 'vakt' } as const;
const QUARTER = 15 * MIN;
/** the name column: 180 px beside the plan, 112 px on a phone so the timeline keeps most of the width */
const NAMES = 180;
const PHONE_NAMES = 112;
/** a shift's place in its row: a 40 px card, a 2 px gap, the 36 px activity strip and 8 px air (V19) */
const LEVEL = 86;
/** Where an empty day opens: the working morning, not midnight. */
const EMPTY_FROM = 7 * 60;
const EMPTY_TO = 17 * 60;
const at = (ms: number) => new Date(ms).toISOString();
/** The Oslo clock of an instant, for labels. */
const clock = (ms: number) => oslo(at(ms))?.time ?? '';

/** What a gesture on the activity lane hands over: nothing is saved until the panel saves it. */
export type LaneChange =
  | { task: number; start: string; end: string }
  | { add: true; start: string; end: string }
  | { lunch: string; minutes: number }
  /** the divider between two adjoining tasks moved: the first ends and the second starts at `at` */
  | { split: [number, number]; at: string };
/** One activity on a shift's lane, as instants. */
type Act = {
  key: string;
  kind: 'task' | 'lunch';
  id?: number;
  name: string;
  a: number;
  b: number;
  sugg?: boolean;
  /** the library's chosen icon, null for automatic */
  icon?: string | null;
};
/** A gesture on the lane in progress: the activity's range now, and what it started as. */
type Gesture = {
  sid: number;
  key: string;
  kind: 'task' | 'lunch' | 'draw' | 'split';
  /** a divider's second task: its id, name and end, which stays */
  r?: { id: number; name: string; b: number };
  id?: number;
  name: string;
  mode: 'move' | 'start' | 'end';
  a: number;
  b: number;
  a0: number;
  b0: number;
  /** where on the activity it was taken, so it does not jump under the pointer */
  grab: number;
  x0: number;
  moved: boolean;
  /** the shift's part on this day, in which the activity has to stay */
  lo: number;
  hi: number;
  rect: { left: number; width: number };
};

export function DayView({
  date,
  users,
  shifts,
  editable,
  onMove,
  onResize,
  entriesFor,
  busy,
  onOpen,
  onEdit,
  onActivity,
  selected = null,
  lunchMinutes = 30,
  phone = false,
  onAdd,
  squeezed = false,
  band = null,
  onFix,
  onPlanLunch,
}: {
  date: string;
  users: ApiUser[];
  shifts: ApiShift[];
  editable: boolean;
  /** `toClock` is where on the day it was dropped; without it the time is kept. */
  onMove: (id: number, toUser: number | null, toDate: string, toClock?: string) => void;
  /** the shift's new start and end, both full timestamps; the edge not dragged is the stored one (review F02) */
  onResize: (id: number, start: string, end: string) => void;
  entriesFor: (s: ApiShift) => ReactNode;
  busy: (id: number) => boolean;
  /** opens the shift in the panel; a task on its lane opens that task for editing */
  /** a task opens the editor at it; a lunch or a task without a time at «Aktiviteter og pause» (#153, VF-06) */
  onOpen?: (
    s: ApiShift,
    focus?: { task?: number; also?: { task: number }; section?: 'oppgaver' | 'vakt' },
  ) => void;
  /** opens the shift's activities in the panel */
  onEdit?: (s: ApiShift) => void;
  /** a task or lunch dragged, resized or drawn on the lane: opened in the panel, to be saved there */
  onActivity?: (s: ApiShift, change: LaneChange) => void;
  /** the company's break length, for the suggested lunch */
  lunchMinutes?: number;
  /** the shift open in the panel or the editor: kept in view beside it (retest R06) */
  selected?: number | null;
  /** a phone (V19 mobile): the day opens as a list per person, the timeline one tap away */
  phone?: boolean;
  /** the + on a person's empty row (gap A1): where it was pressed, for the shift-type chooser */
  onAdd?: (userId: number, at: { x: number; y: number }) => void;
  /** a panel is open beside the plan: «Hele dagen» fits the whole working window to what is left (gap T3) */
  squeezed?: boolean;
  /** the clock range being edited on the selected shift (an activity), marked across every row; the
      selected shift's own hours when none is given */
  band?: { start: string; end: string } | null;
  /** the one-click way out of two overlapping activities: the task's new clock times, saved at once */
  onFix?: (s: ApiShift, task: number, start: string, end: string) => void;
  /** the suggested lunch made the planned one: its start (an instant) and minutes, saved at once */
  onPlanLunch?: (s: ApiShift, start: string, minutes: number) => void;
}) {
  const track = useRef<HTMLDivElement | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState<'dag' | 'stor'>('dag');
  // on a phone the day opens as «Oversikt», a list per person; «Tidslinje» is the plan on a time scale
  const [mode, setMode] = useState<'liste' | 'tid'>('liste');
  const list = phone && mode === 'liste';
  const names = phone ? PHONE_NAMES : NAMES;
  const [width, setWidth] = useState(1080);
  const [drag, setDrag] = useState<{ id: number; edge: 'start' | 'end'; start: number; end: number } | null>(
    null,
  );
  const [gest, setGest] = useState<Gesture | null>(null);
  // the shift whose suggested lunch is open with «Planlegg lunsj»
  const [lunchFor, setLunchFor] = useState<number | null>(null);
  // a release after a drag is not also a click on the activity it started on
  const dragged = useRef(false);
  const busyGesture = !!drag || !!gest;
  useEffect(() => {
    if (!busyGesture) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setDrag(null);
      // the release that follows a cancelled drag is not a click either
      setGest((g) => {
        if (g?.moved) dragged.current = true;
        return null;
      });
    };
    window.addEventListener('keydown', esc, true);
    return () => window.removeEventListener('keydown', esc, true);
  }, [busyGesture]);
  // the day as two instants: 23 or 25 hours on the days the clocks change
  const day0 = Date.parse(osloISO(date, '00:00'));
  const day1 = Date.parse(osloISO(addDays(date, 1), '00:00'));
  const DAY = (day1 - day0) / MIN;
  const pct = (m: number) => `${(m / DAY) * 100}%`;
  const minOf = (ms: number) => (ms - day0) / MIN;
  // every shift that touches this day, one that started yesterday or ends tomorrow included
  const onDay = shifts.filter((s) => Date.parse(s.start_time) < day1 && Date.parse(s.end_time) > day0);
  const rows: { id: number | null; label: string; user: ApiUser | null }[] = [
    { id: null, label: T('Ledige vakter'), user: null },
    ...users.map((u) => ({ id: u.id, label: fullName(u), user: u })),
  ];

  /** The whole shift, and the part of it this day shows, in this day's minutes. */
  const span = (s: ApiShift) => {
    const a = Date.parse(s.start_time);
    const b = Date.parse(s.end_time);
    const from = minOf(Math.max(a, day0));
    const to = minOf(Math.min(b, day1));
    return { a, b, from, to: Math.max(to, from + 15), before: a < day0, after: b > day1 };
  };
  // the day's working window: an hour before the first shift to an hour after the last
  // a night shift carried in from yesterday does not pull the window to midnight (T5): it is a
  // scroll away, as every hour is; only a day of nothing but carried-in work opens on it
  const own = onDay.filter((s) => !span(s).before);
  const winFrom = onDay.length
    ? Math.max(0, Math.min(...(own.length ? own : onDay).map((s) => span(s).from)) - 60)
    : EMPTY_FROM;
  const winTo = onDay.length ? Math.min(DAY, Math.max(...onDay.map((s) => span(s).to)) + 60) : EMPTY_TO;
  const winHours = Math.max(4, (winTo - winFrom) / 60);
  // "Hele dagen" fits the working window to the width; "Større felt" doubles it. All 24 hours scroll.
  // beside an open panel the working window still fits: down to 16 px an hour rather than scrolling
  // the edited shift out of view (gap T3); «Større felt» keeps its own width and scrolls
  const floor = squeezed && scale === 'dag' ? 16 : 44;
  const perHour = Math.max(floor, (width - names) / winHours) * (scale === 'stor' ? 2 : 1);
  const laneWidth = Math.round(perHour * (DAY / 60));
  const step = perHour < 24 ? 3 : perHour < 56 ? 2 : 1;

  // the width to fit is the page column's, measured from outside the scroller: the scroller grows
  // with the lane it holds, so measuring it would widen the scale on every pass
  const root = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const fit = () => {
      const w = root.current?.parentElement?.clientWidth;
      if (w) setWidth(w);
    };
    fit();
    window.addEventListener('resize', fit);
    // the column also narrows when a panel opens beside it, with no window resize
    const parent = root.current?.parentElement;
    const ro = typeof ResizeObserver === 'function' && parent ? new ResizeObserver(fit) : null;
    if (ro && parent) ro.observe(parent);
    return () => {
      window.removeEventListener('resize', fit);
      ro?.disconnect();
    };
  }, []);
  // opens on the working window, once per day and scale; the person's own scrolling stays after that
  const scrolledFor = useRef('');
  useEffect(() => {
    // a new scale (a panel opened or closed) opens on the window again
    const key = `${date}|${scale}|${onDay.length > 0}|${Math.round(perHour)}`;
    const el = scroller.current;
    // the phone's list has no scroller: the timeline, once shown, opens on the window too
    if (scrolledFor.current === key || !el) return;
    scrolledFor.current = key;
    el.scrollLeft = (winFrom / 60) * perHour;
  });

  // the shift being worked on stays in view, its row and its hours, inside the day's own scroll
  // region, so the ruler and the names stay put around it (retest R06)
  useEffect(() => {
    if (selected == null) return;
    const el = root.current?.querySelector<HTMLElement>(`[data-slot-id="${selected}"]`);
    el?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [selected]);

  /** The instant a pointer is over, snapped to the quarter hour. */
  const instantAt = (clientX: number) => {
    const r = track.current?.getBoundingClientRect();
    if (!r) return null;
    const m = Math.max(0, Math.min(DAY, ((clientX - r.left) / r.width) * DAY));
    return day0 + Math.round((m * MIN) / QUARTER) * QUARTER;
  };
  const beginResize = (s: ApiShift, edge: 'start' | 'end') => (e: React.PointerEvent) => {
    if (!editable || s.punch_in_time || busy(s.id)) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDrag({ id: s.id, edge, start: Date.parse(s.start_time), end: Date.parse(s.end_time) });
  };
  const snap = (x: number) => day0 + Math.round((x - day0) / QUARTER) * QUARTER;
  const onPointerMove = (e: React.PointerEvent) => {
    if (gest) return moveGesture(e.clientX);
    if (!drag) return;
    const x = instantAt(e.clientX);
    if (x == null) return;
    // only the dragged edge moves; the other keeps its stored instant, the next day's end included
    setDrag((d) => {
      if (!d) return d;
      if (d.edge === 'start') return x <= d.end - QUARTER ? { ...d, start: x } : d;
      return x >= d.start + QUARTER ? { ...d, end: x } : d;
    });
  };
  /** A gesture the person abandoned writes nothing. */
  const cancelDrag = () => {
    setDrag(null);
    setGest(null);
  };
  const endResize = () => {
    if (gest) return endGesture();
    if (!drag) return;
    const d = drag;
    setDrag(null);
    const s = onDay.find((x) => x.id === d.id);
    if (!s) return;
    if (Date.parse(s.start_time) === d.start && Date.parse(s.end_time) === d.end) return; // nothing moved
    onResize(d.id, at(d.start), at(d.end));
  };

  /** A shift's lunch (planned, or suggested) and its timed tasks, as instants. */
  const actsOf = (s: ApiShift): Act[] => {
    const sa = Date.parse(s.start_time);
    const out: Act[] = [];
    const lunch = plannedLunch(s);
    if (lunch && s.planned_lunch_start) {
      const a = Date.parse(s.planned_lunch_start);
      out.push({ key: 'lunch', kind: 'lunch', name: T('Lunsj'), a, b: a + lunch.minutes * MIN });
    } else {
      const hint = suggestLunch(s, lunchMinutes);
      if (hint) {
        const a = Date.parse(hint.start);
        out.push({ key: 'lunch', kind: 'lunch', name: T('Lunsj'), a, b: a + hint.minutes * MIN, sugg: true });
      }
    }
    // a task's times are clock times on the shift (TIME 'HH:MM:SS'); one earlier than the shift's
    // start is the next day, as the server reads it (taskFitsShift)
    const startDay = oslo(s.start_time)?.date ?? date;
    const instant = (hhmm: string, notBefore: number) => {
      const x = Date.parse(osloISO(startDay, hhmm));
      return x < notBefore ? Date.parse(osloISO(addDays(startDay, 1), hhmm)) : x;
    };
    for (const t of s.taskAssignments ?? []) {
      if (!t.has_time_range || !t.start_time || !t.end_time) continue;
      const a = instant(t.start_time.slice(0, 5), sa);
      out.push({
        key: `t${t.id}`,
        kind: 'task',
        id: t.id,
        name: t.shiftTask?.name ?? T('Aktivitet'),
        icon: t.shiftTask?.icon ?? null,
        a,
        b: instant(t.end_time.slice(0, 5), a + MIN),
      });
    }
    return out;
  };
  /**
   * The tasks a range would sit on top of. A lunch, planned or suggested, is a pause in what it lies
   * on, never a clash (owner, 2026-10-18): a lunch dragged over a task, or a task over the lunch, is fine.
   */
  const overlapsOf = (s: ApiShift, key: string, a: number, b: number) =>
    key === 'lunch' ? [] : actsOf(s).filter((x) => x.key !== key && x.kind === 'task' && x.a < b && x.b > a);

  const beginGesture =
    (s: ApiShift, kind: Gesture['kind'], mode: Gesture['mode'], x?: Act) => (e: React.PointerEvent) => {
      if (!editable || !onActivity || busy(s.id) || e.button !== 0) return;
      // drawing is a mouse or pen gesture; on touch the lane scrolls and + adds (review, completion list)
      if (kind === 'draw' && e.pointerType === 'touch') return;
      const laneEl = (e.currentTarget as Element).closest('.dlane');
      const r = laneEl?.getBoundingClientRect();
      if (!r?.width) return;
      const lo = Math.max(Date.parse(s.start_time), day0);
      const hi = Math.min(Date.parse(s.end_time), day1);
      const rect = { left: r.left, width: r.width };
      const p = lo + ((e.clientX - r.left) / r.width) * (hi - lo);
      const a = x ? x.a : Math.max(lo, Math.min(hi, snap(p)));
      const b = x ? x.b : a;
      // an activity reaching onto the other day is moved there or in the panel
      if (x && (x.a < lo || x.b > hi)) return;
      e.stopPropagation();
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      dragged.current = false;
      setGest({
        sid: s.id,
        key: x?.key ?? 'new',
        kind,
        ...(x?.id != null ? { id: x.id } : {}),
        name: x?.name ?? T('Ny aktivitet'),
        mode,
        a,
        b,
        a0: a,
        b0: b,
        grab: p - a,
        x0: e.clientX,
        moved: false,
        lo,
        hi,
        rect,
      });
    };
  /** Two timed tasks back to back: the divider between them (V19 "Fordel tiden"). Lunch keeps its time. */
  const pairsOf = (s: ApiShift) => {
    const tasks = actsOf(s)
      .filter((x) => x.kind === 'task')
      .sort((x, y) => x.a - y.a);
    return tasks.flatMap((l, i) => tasks.filter((r, j) => j !== i && r.a === l.b).map((r) => ({ l, r })));
  };
  const beginSplit = (s: ApiShift, l: Act, r: Act) => (e: React.PointerEvent) => {
    if (!editable || !onActivity || busy(s.id) || e.button !== 0) return;
    const laneEl = (e.currentTarget as Element).closest('.dlane');
    const rect = laneEl?.getBoundingClientRect();
    if (!rect?.width) return;
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    dragged.current = false;
    const lo = Math.max(Date.parse(s.start_time), day0);
    const hi = Math.min(Date.parse(s.end_time), day1);
    setGest({
      sid: s.id,
      key: l.key,
      kind: 'split',
      id: l.id as number,
      name: l.name,
      r: { id: r.id as number, name: r.name, b: r.b },
      mode: 'end',
      a: l.a,
      b: l.b,
      a0: l.a,
      b0: l.b,
      grab: 0,
      x0: e.clientX,
      moved: false,
      lo,
      hi,
      rect: { left: rect.left, width: rect.width },
    });
  };
  const moveGesture = (clientX: number) => {
    setGest((g) => {
      if (!g) return g;
      const moved = g.moved || Math.abs(clientX - g.x0) > 4;
      if (!moved) return g;
      const p = g.lo + ((clientX - g.rect.left) / g.rect.width) * (g.hi - g.lo);
      // a lunch is 5 to 180 minutes (the server's bad_lunch); a task at least a quarter
      const longest = g.kind === 'lunch' ? 180 * MIN : Number.POSITIVE_INFINITY;
      let { a, b } = g;
      if (g.kind === 'split' && g.r) {
        // at least a quarter each side; the outer ends stay
        b = Math.max(g.a + QUARTER, Math.min(g.r.b - QUARTER, snap(p)));
      } else if (g.mode === 'move') {
        const len = g.b0 - g.a0;
        a = Math.max(g.lo, Math.min(g.hi - len, snap(p - g.grab)));
        b = a + len;
      } else if (g.kind === 'draw') {
        const x = Math.max(g.lo, Math.min(g.hi, snap(p)));
        a = Math.min(g.a0, x);
        b = Math.max(g.a0, x);
      } else if (g.mode === 'start') {
        a = Math.max(g.lo, g.b - longest, Math.min(g.b - QUARTER, snap(p)));
      } else {
        b = Math.min(g.hi, g.a + longest, Math.max(g.a + QUARTER, snap(p)));
      }
      return { ...g, a, b, moved };
    });
  };
  const endGesture = () => {
    const g = gest;
    setGest(null);
    if (!g?.moved) return;
    dragged.current = true;
    const s = onDay.find((x) => x.id === g.sid);
    if (!s || !onActivity) return;
    if (g.kind === 'draw') {
      if (g.b - g.a >= QUARTER) onActivity(s, { add: true, start: clock(g.a), end: clock(g.b) });
      return;
    }
    if (g.a === g.a0 && g.b === g.b0) return; // back where it was
    if (g.kind === 'split' && g.r && g.id != null)
      return onActivity(s, { split: [g.id, g.r.id], at: clock(g.b) });
    if (g.kind === 'lunch') onActivity(s, { lunch: at(g.a), minutes: Math.round((g.b - g.a) / MIN) });
    else if (g.id != null) onActivity(s, { task: g.id, start: clock(g.a), end: clock(g.b) });
  };
  /** A click that ends a drag opened nothing more than the drag did. */
  const click = (f: () => void) => () => {
    if (dragged.current) {
      dragged.current = false;
      return;
    }
    f();
  };

  /**
   * A new activity on a shift, where nobody pointed (the shift's +, the list's «Legg til aktivitet»):
   * the first free half hour from its start, beside the planned tasks and lunch (gap A2). It opens the
   * library to pick from, with Fra and Til filled; nothing is saved before «Lagre endring».
   */
  const addAct = (s: ApiShift) => {
    if (!onActivity) return onEdit?.(s);
    const taken = actsOf(s)
      .filter((x) => !x.sugg)
      .map((x) => ({ start_time: clock(x.a), end_time: clock(x.b) }));
    const slot = freeSlot(clock(Date.parse(s.start_time)), clock(Date.parse(s.end_time)), taken);
    onActivity(
      s,
      slot ? { add: true, start: slot.start_time, end: slot.end_time } : { add: true, start: '', end: '' },
    );
  };

  /** What an activity is, when, and for how long: the details on hover and keyboard focus, and the list's line. */
  const what = (x: Act) =>
    x.sugg ? T('Forslag til lunsj') : x.kind === 'lunch' ? T('Planlagt lunsj') : x.name;
  const length = (x: Act) => T('{n} min', { n: Math.round((x.b - x.a) / MIN) });
  const detail = (x: Act) => `${what(x)} · ${clock(x.a)}–${clock(x.b)} · ${length(x)}`;

  /**
   * The shift's activities as a list, under the person's name (#154 VF-08): every one of them, short
   * and untimed ones included, on a control of its own off the time track, by click, tap or keyboard.
   * Each line opens what the segment opens; the last adds one, for who may plan.
   */
  const actList = (s: ApiShift, who: string, many: boolean) => {
    const acts = [...actsOf(s)].sort((p, q) => p.a - q.a || p.b - q.b);
    const untimed = (s.taskAssignments ?? []).filter(
      (t) => !(t.has_time_range && t.start_time && t.end_time),
    );
    const n = acts.length + untimed.length;
    const add = editable && !!(onActivity || onEdit);
    if (!n && !add) return null;
    const when = `${clock(span(s).a)}–${clock(span(s).b)}`;
    return (
      <Popover.Root key={s.id}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={ds.list}
            data-testid={`act-list-${s.id}`}
            aria-label={T('Aktiviteter for {who}, {when}: {n}', { who, when, n })}
          >
            <Icon name="list" />
            {T('Aktiviteter')}
            <small>{n}</small>
            {/* a person with two shifts has two lists: each says which, on its own line, inside the name column */}
            {many && <small className={ds.when}>{when}</small>}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={`pop dmulti ${ds.pop}`}
            sideOffset={4}
            align="start"
            collisionPadding={8}
            data-testid="act-list"
          >
            <h6>{T('Aktiviteter, {who} {when}', { who, when })}</h6>
            {acts.map((x) => (
              <Popover.Close asChild key={x.key}>
                <button
                  type="button"
                  className="pr"
                  onClick={() =>
                    x.kind === 'task' && x.id != null ? onOpen?.(s, { task: x.id }) : onOpen?.(s, PAUSE)
                  }
                >
                  <span>{what(x)}</span>
                  <span>
                    {clock(x.a)}–{clock(x.b)} · {length(x)}
                  </span>
                </button>
              </Popover.Close>
            ))}
            {untimed.map((t) => (
              <Popover.Close asChild key={`u${t.id}`}>
                <button type="button" className="pr" onClick={() => onOpen?.(s, PAUSE)}>
                  <span>{t.shiftTask?.name ?? T('Aktivitet')}</span>
                  <span>{T('uten tid')}</span>
                </button>
              </Popover.Close>
            ))}
            {add && (
              <Popover.Close asChild>
                <button
                  type="button"
                  className={`pr ${ds.add}`}
                  data-testid="act-list-add"
                  onClick={() => addAct(s)}
                >
                  <span>+ {T('Legg til aktivitet')}</span>
                </button>
              </Popover.Close>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    );
  };

  /** Overlapping shifts in one row each get their own level, so none hides under another. */
  const levels = (list: ApiShift[]) => {
    const ends: number[] = [];
    const at = new Map<number, number>();
    for (const s of [...list].sort((a, b) => span(a).from - span(b).from)) {
      const { from, to } = span(s);
      let l = ends.findIndex((e) => e <= from);
      if (l < 0) l = ends.length;
      ends[l] = to;
      at.set(s.id, l);
    }
    return { at, n: Math.max(1, ends.length) };
  };

  /**
   * What overlaps what, and one click to part them (owner, 2026-10-18): the later task starts when
   * the earlier ends, or is shortened to the shift's end; with no room the reason is said and the
   * rows below stay the way to edit by hand. The fix saves at once through the page's write.
   */
  const clashBox = (s: ApiShift, clashes: Clash[], Close: CloseLike) =>
    clashes.map((c, i) => {
      const f = c.fix;
      return (
        <div className={ds.clashRow} key={`${c.first.key}-${c.later.key}`} data-testid="act-clash">
          <p>
            {T('{a} og {b} overlapper {from}–{to}', {
              a: what(c.first as Act),
              b: what(c.later as Act),
              from: clock(c.from),
              to: clock(c.to),
            })}
          </p>
          {f.kind === 'none' ? (
            <p className={ds.clashWhy} data-testid="act-clash-none">
              {f.why === 'lunch'
                ? T('Lunsjen flyttes ikke av seg selv. Endre tidene for hånd under.')
                : T('{name} får ikke plass etter {other} før vakten slutter. Endre tidene for hånd under.', {
                    name: c.later.name,
                    other: what(c.first as Act),
                  })}
            </p>
          ) : // one fix offered at a time: the next overlap is worked out again once this one is saved
          i === 0 && editable && onFix && !busy(s.id) ? (
            <Close asChild>
              <button
                type="button"
                className={ds.fix}
                data-testid="act-clash-fix"
                onClick={() => onFix(s, f.id, clock(f.a), clock(f.b))}
              >
                {f.kind === 'move'
                  ? T('Flytt {name} til {at}', { name: f.name, at: clock(f.a) })
                  : T('Forkort {name} til {from}–{to}', { name: f.name, from: clock(f.a), to: clock(f.b) })}
              </button>
            </Close>
          ) : null}
        </div>
      );
    });

  /** One shift's activity lane: its lunch (planned, or suggested) and its tasks, on the part of it this day shows. */
  const lane = (s: ApiShift, who: string) => {
    const { a: sa, b: sb } = span(s);
    const vf = Math.max(sa, day0);
    const vt = Math.min(sb, day1);
    /** Left and width inside the visible part; nothing when the activity falls on the other day. */
    const place = (x: number, y: number) =>
      y <= vf || x >= vt
        ? null
        : {
            left: `${((Math.max(x, vf) - vf) / (vt - vf)) * 100}%`,
            width: `${Math.max(3, ((Math.min(y, vt) - Math.max(x, vf)) / (vt - vf)) * 100)}%`,
          };
    // the lunch, planned or suggested, is always on the strip, drawn over the task it falls in: the
    // task pauses there (owner, 2026-10-18). It is never part of a clash, so it is drawn last, on top
    const tasks = actsOf(s).filter((x) => x.kind === 'task');
    const acts = [...tasks, ...actsOf(s).filter((x) => x.kind === 'lunch')];
    // tasks that overlap in time share one segment with their count, and it lists every one of them:
    // on a single lane the longer one would otherwise cover the shorter (retest R03)
    const groups: Act[][] = [];
    for (const x of [...tasks].sort((p, q) => p.a - q.a || p.b - q.b)) {
      const last = groups.at(-1);
      if (last && x.a < Math.max(...last.map((y) => y.b))) last.push(x);
      else groups.push([x]);
    }
    const crowded = new Set(
      groups
        .filter((grp) => grp.length > 1)
        .flat()
        .map((x) => x.key),
    );
    // a segment too short for its words shows a mark; the name and time are in its label, its details
    // (hover and keyboard focus) and the shift's activity list (review V03, #154 VF-08)
    const tiny = (ms: number) => (ms / 36e5) * perHour < 44;
    // the words show only where they fit whole: a 30 minute lunch was «Luns…» (gap T1)
    const px = (x: Act) => ((x.b - x.a) / 36e5) * perHour;
    // the icon from 20 px, the words beside it only where they fit whole (gaps T1, T2)
    const fits = (x: Act, words: string) => px(x) >= words.length * 7 + 32;
    const face = (x: Act, words: string) => (
      <>
        {px(x) >= 20 ? <Icon name={activityIcon(x.name, x.kind === 'lunch', x.icon)} /> : null}
        {fits(x, words) ? <span>{words}</span> : null}
      </>
    );
    const g = gest?.sid === s.id ? gest : null;
    const can = editable && !!onActivity && !busy(s.id);
    // the edges a segment can be pulled by; none on a mark too short to hold them
    const pairs = pairsOf(s).filter((p) => !crowded.has(p.l.key) && !crowded.has(p.r.key));
    const handles = (x: Act) =>
      can && !tiny(x.b - x.a) ? (
        <>
          {/* an edge shared with the next task is the divider's, below */}
          {pairs.some((p) => p.r.key === x.key) ? null : (
            <span
              className={`${ds.dh} ${ds.l}`}
              aria-hidden="true"
              data-testid={`act-start-${x.key}-${s.id}`}
              onPointerDown={beginGesture(s, x.kind, 'start', x)}
            />
          )}
          {pairs.some((p) => p.l.key === x.key) ? null : (
            <span
              className={`${ds.dh} ${ds.r}`}
              aria-hidden="true"
              data-testid={`act-end-${x.key}-${s.id}`}
              onPointerDown={beginGesture(s, x.kind, 'end', x)}
            />
          )}
        </>
      ) : null;
    const ghostBad = g?.moved && g.kind !== 'split' ? overlapsOf(s, g.key, g.a, g.b).length > 0 : false;
    return (
      <div
        className={`dlane ${ds.lane}${can ? ` ${ds.can}` : ''}`}
        data-testid={`lane-${s.id}`}
        onPointerDown={(e) => e.target === e.currentTarget && beginGesture(s, 'draw', 'end')(e)}
      >
        {acts.map((x) => {
          const style = place(x.a, x.b);
          if (!style || crowded.has(x.key)) return null;
          const short = tiny(x.b - x.a);
          const when = `${clock(x.a)}–${clock(x.b)}`;
          const moving = g?.moved && (g.key === x.key || g.r?.id === x.id) ? ` ${ds.moving}` : '';
          const sz = `${short ? ` ${ds.tiny}` : ''}${px(x) < 20 ? ` ${ds.dot}` : ''}`;
          const down = can ? { onPointerDown: beginGesture(s, x.kind, 'move', x) } : {};
          if (x.kind === 'lunch' && !x.sugg)
            return (
              <Tip key={x.key} text={detail(x)}>
                <button
                  type="button"
                  className={`dact lunch ${ds.act} ${ds.lunch}${sz}${moving}`}
                  data-testid={`lunch-${s.id}`}
                  style={style}
                  aria-label={`${T('Planlagt lunsj')} ${when}, ${who}`}
                  onClick={click(() => onOpen?.(s, PAUSE))}
                  {...down}
                >
                  {face(x, T('Lunsj'))}
                  {handles(x)}
                </button>
              </Tip>
            );
          if (x.kind === 'lunch') {
            const hint = (
              <button
                type="button"
                className={`dact lunch sugg ${ds.act} ${ds.lunch} ${ds.sugg}${sz}${moving}`}
                data-testid={`lunch-hint-${s.id}`}
                style={style}
                aria-label={`${T('Forslag til lunsj')} ${when}, ${who}`}
                // who may plan gets «Planlegg lunsj»; a reader opens the shift's break section as before
                onClick={click(() => (editable && onPlanLunch ? setLunchFor(s.id) : onOpen?.(s, PAUSE)))}
                {...down}
              >
                {face(x, T('Lunsj?'))}
                {handles(x)}
              </button>
            );
            const Close = phone ? Dialog.Close : Popover.Close;
            const body = (
              <>
                <h6>{T('Forslag til lunsj, {who}', { who })}</h6>
                <p className={ds.lunchWhen}>
                  {when} · {length(x)}
                </p>
                <Close asChild>
                  <button
                    type="button"
                    className={ds.fix}
                    data-testid="lunch-plan"
                    onClick={() => onPlanLunch?.(s, at(x.a), Math.round((x.b - x.a) / MIN))}
                  >
                    {T('Planlegg lunsj')}
                  </button>
                </Close>
                <Close asChild>
                  <button type="button" className="pr" onClick={() => onOpen?.(s, PAUSE)}>
                    <span>{T('Endre tid eller lengde')}</span>
                  </button>
                </Close>
              </>
            );
            const open = lunchFor === s.id;
            const toggle = (o: boolean) => setLunchFor(o ? s.id : null);
            // its own class: the page's global .hint margin flattened it to 4 px (review V01)
            return phone ? (
              <Dialog.Root key={x.key} open={open} onOpenChange={toggle}>
                {hint}
                <Dialog.Portal>
                  <Dialog.Overlay className="scrim" />
                  <Dialog.Content className="vsheet" aria-describedby={undefined}>
                    <Dialog.Title className="sr">{T('Forslag til lunsj, {who}', { who })}</Dialog.Title>
                    <div className={`vpanel dmulti ${ds.sheet}`} data-testid="lunch-pick">
                      {body}
                    </div>
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>
            ) : (
              <Popover.Root key={x.key} open={open} onOpenChange={toggle}>
                <Tip text={detail(x)}>
                  <Popover.Anchor asChild>{hint}</Popover.Anchor>
                </Tip>
                <Popover.Portal>
                  <Popover.Content
                    className={`pop dmulti ${ds.pop}`}
                    sideOffset={4}
                    collisionPadding={8}
                    data-testid="lunch-pick"
                  >
                    {body}
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            );
          }
          const t = (s.taskAssignments ?? []).find((y) => y.id === x.id);
          return (
            <Tip key={x.key} text={detail(x)}>
              <button
                type="button"
                className={`dact task ${ds.act} ${ds.task}${t?.status === 'completed' ? ` ${ds.done}` : ''}${sz}${moving}`}
                data-testid={`act-${x.key}`}
                style={style}
                aria-label={`${T('Aktivitet')} ${x.name} ${when}, ${who}`}
                onClick={click(() => onOpen?.(s, { task: x.id as number }))}
                {...down}
              >
                {face(x, x.name)}
                {handles(x)}
              </button>
            </Tip>
          );
        })}
        {groups
          .filter((grp) => grp.length > 1)
          .map((grp, i) => {
            const a = Math.min(...grp.map((x) => x.a));
            const b = Math.max(...grp.map((x) => x.b));
            const style = place(a, b);
            if (!style) return null;
            const named = (x: Act) => `${what(x)} ${clock(x.a)}–${clock(x.b)}`;
            const trigger = (
              <button
                key="trigger"
                type="button"
                className={`dact multi ${ds.act} ${ds.multi} ${ds.clash}${tiny(b - a) ? ` ${ds.tiny}` : ''}`}
                style={style}
                data-testid={`act-multi-${s.id}-${i}`}
                aria-label={T('{n} aktiviteter samtidig: {list}, {who}', {
                  n: grp.length,
                  list: grp.map(named).join(', '),
                  who,
                })}
              >
                <span aria-hidden="true">{grp.length}</span>
                {tiny(b - a) ? '' : ` ${T('samtidig')}`}
              </button>
            );
            const Close = phone ? Dialog.Close : Popover.Close;
            const body = (
              <>
                <h6 key="title">{T('Samtidig, {who}', { who })}</h6>
                {clashBox(s, clashesOf(grp, Date.parse(s.end_time)), Close)}
                {grp.map((x) => (
                  <Close asChild key={x.key}>
                    <button
                      type="button"
                      className="pr"
                      onClick={() =>
                        x.kind === 'task' && x.id != null ? onOpen?.(s, { task: x.id }) : onOpen?.(s, PAUSE)
                      }
                    >
                      <span>{what(x)}</span>
                      <span>
                        {clock(x.a)}–{clock(x.b)}
                      </span>
                    </button>
                  </Close>
                ))}
              </>
            );
            // on a phone the list is a sheet from the bottom, as the editor is: a popover beside a
            // block scrolled half out of a 390 px plan opened off the screen (owner, 2026-10-18)
            return phone ? (
              <Dialog.Root key={grp[0]?.key}>
                <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
                <Dialog.Portal>
                  <Dialog.Overlay className="scrim" />
                  <Dialog.Content className="vsheet" aria-describedby={undefined}>
                    <Dialog.Title className="sr">{T('Samtidig, {who}', { who })}</Dialog.Title>
                    <div className={`vpanel dmulti ${ds.sheet}`} data-testid="act-multi-list">
                      {body}
                    </div>
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>
            ) : (
              <Popover.Root key={grp[0]?.key}>
                <Tip text={grp.map(detail).join(' · ')}>
                  <Popover.Trigger asChild>{trigger}</Popover.Trigger>
                </Tip>
                <Popover.Portal>
                  <Popover.Content
                    className={`pop dmulti ${ds.pop}`}
                    sideOffset={4}
                    collisionPadding={8}
                    data-testid="act-multi-list"
                  >
                    {body}
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            );
          })}
        {can &&
          pairs.map(({ l, r }) => {
            const where = place(l.b, l.b + MIN);
            if (!where) return null;
            const step = (by: number) => {
              const x = Math.max(l.a + QUARTER, Math.min(r.b - QUARTER, l.b + by));
              if (x !== l.b) onActivity?.(s, { split: [l.id as number, r.id as number], at: clock(x) });
            };
            return (
              <button
                type="button"
                key={`${l.key}-${r.key}`}
                className="dsplit"
                data-testid={`split-${l.key}-${r.key}`}
                style={{ left: where.left }}
                aria-label={T('Fordel tiden mellom {a} og {b}. Skille {t}', {
                  a: l.name,
                  b: r.name,
                  t: clock(l.b),
                })}
                title={T('Dra skillet, bruk piltastene eller trykk for å velge tid')}
                onPointerDown={beginSplit(s, l, r)}
                onClick={click(() => onOpen?.(s, { task: l.id as number, also: { task: r.id as number } }))}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                    e.preventDefault();
                    step(e.key === 'ArrowLeft' ? -QUARTER : QUARTER);
                  }
                }}
              />
            );
          })}
        {g?.moved && g.kind === 'split' && g.r && place(g.b, g.r.b) ? (
          <span className="dghost" aria-hidden="true" style={place(g.b, g.r.b) ?? {}}>
            {((g.r.b - g.b) / 36e5) * perHour < 76 ? clock(g.b) : `${clock(g.b)}–${clock(g.r.b)}`}
          </span>
        ) : null}
        {g?.moved && place(g.a, g.b) ? (
          <span
            className={`dghost${ghostBad ? ' bad' : ''}`}
            data-testid="act-ghost"
            aria-hidden="true"
            style={place(g.a, g.b) ?? {}}
          >
            {/* a short ghost holds its start; the whole range is in the line under the plan */}
            {((g.b - g.a) / 36e5) * perHour < 76 ? clock(g.a) : `${clock(g.a)}–${clock(g.b)}`}
          </span>
        ) : null}
      </div>
    );
  };

  // the time being edited, across every row (V19, gap T3): the chosen activity's range on the
  // selected shift, or the shift's own hours, so the manager sees who else works then
  const marked = (() => {
    const s = selected != null ? onDay.find((x) => x.id === selected) : undefined;
    if (!s) return null;
    const sa = Date.parse(s.start_time);
    const startDay = oslo(s.start_time)?.date ?? date;
    const instant = (hhmm: string, notBefore: number) => {
      const x = Date.parse(osloISO(startDay, hhmm));
      return x < notBefore ? Date.parse(osloISO(addDays(startDay, 1), hhmm)) : x;
    };
    const ok = (c?: string) => !!c && /^\d{2}:\d{2}/.test(c);
    const a = band && ok(band.start) ? instant(band.start.slice(0, 5), sa) : sa;
    const b = band && ok(band.end) ? instant(band.end.slice(0, 5), a + MIN) : Date.parse(s.end_time);
    const from = Math.max(a, day0);
    const to = Math.min(b, day1);
    return to > from ? { left: pct(minOf(from)), width: pct((to - from) / MIN) } : null;
  })();

  /** The + on an empty row: the chooser opens under it (the week's empty cell does the same). */
  const addShift = (r: { id: number | null; label: string; user: ApiUser | null }) =>
    editable && onAdd && r.user ? (
      <button
        type="button"
        className={ds.addShift}
        data-testid={`day-add-${r.id}`}
        aria-label={T('Ny vakt for {who} {d}', { who: r.label, d: fmtDs(date) })}
        onClick={(e) => {
          const b = e.currentTarget.getBoundingClientRect();
          onAdd(r.id as number, { x: b.left, y: b.bottom + 4 });
        }}
      >
        <Icon name="plus" />
        {phone ? T('Legg til vakt') : null}
      </button>
    ) : null;

  /**
   * The phone's «Oversikt» (V19 mobile): one block per person with each shift's card, its activities
   * and lunch as rows, and «+ Legg til aktivitet»; every row opens what the timeline's segment opens.
   */
  const personList = () => (
    <div className={ds.plist} data-testid="day-list">
      {rows.map((r) => {
        const mine = onDay
          .filter((s) => (s.user_id ?? null) === r.id)
          .sort((p, q) => span(p).from - span(q).from);
        // the open row only when there is something to cover
        if (!r.user && !mine.length) return null;
        const planned = mine.reduce((a, s) => a + (span(s).to - span(s).from) / 60, 0);
        return (
          <section
            key={r.id ?? 'open'}
            className={ds.prow}
            data-testid="day-person"
            data-user={r.id ?? 'open'}
            aria-label={r.label}
          >
            <div className={ds.phead}>
              {r.user ? (
                <span className="av" aria-hidden="true">
                  {initials(r.label)}
                </span>
              ) : null}
              <span>
                <b>{r.label}</b>
                <small>
                  {r.user
                    ? T('{h} t planlagt i dag', { h: num(Math.round(planned * 10) / 10) })
                    : T('{n} å dekke', { n: mine.length })}
                </small>
              </span>
            </div>
            {mine.map((s) => {
              const acts = [...actsOf(s)].sort((p, q) => p.a - q.a || p.b - q.b);
              const sugg = acts.find((x) => x.sugg);
              const untimed = (s.taskAssignments ?? []).filter(
                (t) => !(t.has_time_range && t.start_time && t.end_time),
              );
              return (
                <div key={s.id} className={ds.pshift} data-slot-id={s.id} data-testid="day-list-shift">
                  {entriesFor(s)}
                  {clashBox(
                    s,
                    clashesOf(
                      acts.filter((x) => x.kind === 'task'),
                      Date.parse(s.end_time),
                    ),
                    Keep,
                  )}
                  {acts.map((x) => (
                    <button
                      type="button"
                      key={x.key}
                      className={`${ds.pact}${x.kind === 'lunch' ? ` ${ds.plunch}` : ''}${x.sugg ? ` ${ds.psugg}` : ''}`}
                      data-testid={`day-list-act-${x.key}-${s.id}`}
                      onClick={() =>
                        x.kind === 'task' && x.id != null ? onOpen?.(s, { task: x.id }) : onOpen?.(s, PAUSE)
                      }
                    >
                      <Icon name={activityIcon(x.name, x.kind === 'lunch', x.icon)} />
                      <span>
                        <b>{x.sugg ? T('Lunsj (forslag)') : what(x)}</b>
                        <small>
                          {clock(x.a)}–{clock(x.b)} · {length(x)}
                        </small>
                      </span>
                      <Icon name="chevronRight" />
                    </button>
                  ))}
                  {sugg && editable && onPlanLunch && (
                    <button
                      type="button"
                      className={ds.padd}
                      data-testid={`day-list-plan-lunch-${s.id}`}
                      onClick={() => onPlanLunch(s, at(sugg.a), Math.round((sugg.b - sugg.a) / MIN))}
                    >
                      <Icon name="utensils" /> {T('Planlegg lunsj')}
                    </button>
                  )}
                  {untimed.map((t) => (
                    <button
                      type="button"
                      key={`u${t.id}`}
                      className={ds.pact}
                      onClick={() => onOpen?.(s, PAUSE)}
                    >
                      <span>
                        <b>{t.shiftTask?.name ?? T('Aktivitet')}</b>
                        <small>{T('uten tid')}</small>
                      </span>
                      <Icon name="chevronRight" />
                    </button>
                  ))}
                  {editable && (onActivity || onEdit) && (
                    <button
                      type="button"
                      className={ds.padd}
                      data-testid={`day-list-add-${s.id}`}
                      onClick={() => addAct(s)}
                    >
                      <Icon name="plus" /> {T('Legg til aktivitet')}
                    </button>
                  )}
                </div>
              );
            })}
            {!mine.length && (
              <div className={ds.pnone}>
                <span>{T('Ingen vakt')}</span>
                {addShift(r)}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );

  return (
    <div
      ref={root}
      className={`dayview v19plan${phone ? ` ${ds.phone}` : ''}`}
      data-testid="dayview"
      style={{ '--dn': `${names}px` } as React.CSSProperties}
      onPointerMove={onPointerMove}
      onPointerUp={endResize}
      onPointerCancel={cancelDrag}
      onKeyDown={(e) => e.key === 'Escape' && cancelDrag()}
    >
      <div className={`dhead ${ds.head}`}>
        <b>{T('Dagsplan')}</b>
        <span className="sp" />
        {phone && (
          <fieldset className="dscale" aria-label={T('Visning')} data-testid="day-mode">
            <button type="button" aria-pressed={mode === 'liste'} onClick={() => setMode('liste')}>
              {T('Oversikt')}
            </button>
            <button type="button" aria-pressed={mode === 'tid'} onClick={() => setMode('tid')}>
              {T('Tidslinje')}
            </button>
          </fieldset>
        )}
        {!list && (
          <fieldset className="dscale" aria-label={T('Skala')}>
            <button type="button" aria-pressed={scale === 'dag'} onClick={() => setScale('dag')}>
              {T('Hele dagen')}
            </button>
            <button type="button" aria-pressed={scale === 'stor'} onClick={() => setScale('stor')}>
              {T('Større felt')}
            </button>
          </fieldset>
        )}
        {!list && (
          <span className={ds.legend} aria-hidden="true">
            <i className={ds.vakt} /> {T('Vakt')} <i className={ds.akt} /> {T('Aktivitet')}{' '}
            <i className={ds.lun} /> {T('Lunsj')}
          </span>
        )}
      </div>
      {list ? (
        personList()
      ) : (
        <div
          className="dayscroll"
          data-testid="day-scroll"
          ref={scroller}
          // a segment reached by keyboard is scrolled clear of the sticky names: Chrome's own focus scroll
          // left a partly shown one under them, where the scroller's scroll-padding is honoured by an
          // explicit scroll (VF-09, 640x328 at 200 %); not on a click, so a drag is never moved under the pointer
          onFocus={(e) => {
            const el = e.target as HTMLElement;
            if (!el.matches?.(':focus-visible')) return;
            el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
            // a segment wider than the part of the track in view (a phone shows about 180 px of it) is
            // not moved by «nearest» at all: its start, with the time, is brought out from under the names
            const sc = e.currentTarget;
            const hidden =
              sc.getBoundingClientRect().left +
              (Number.parseFloat(getComputedStyle(sc).scrollPaddingLeft) || 0) -
              el.getBoundingClientRect().left;
            if (hidden > 0) sc.scrollLeft -= hidden;
          }}
        >
          <div className="daylane" style={{ width: names + laneWidth, minWidth: 0 }}>
            <div className="dayhead">
              <span className="dayname">{T('Medarbeider')}</span>
              <div className="dayscale" ref={track}>
                {Array.from({ length: Math.round(DAY / 60) + 1 }, (_, h) => h)
                  .filter((h) => h % step === 0)
                  .map((h) => (
                    <span key={h} style={{ left: pct(h * 60) }}>
                      {h * 60 === DAY ? '24:00' : clock(day0 + h * 60 * MIN)}
                    </span>
                  ))}
              </div>
            </div>
            {rows.map((r) => {
              const mine = onDay.filter((s) => (s.user_id ?? null) === r.id);
              const lv = levels(mine);
              // the part of each shift on this day: 22:00 to 06:00 is two hours here and six tomorrow
              const planned = mine.reduce((a, s) => a + (span(s).to - span(s).from) / 60, 0);
              return (
                <div
                  className="dayrow"
                  key={r.id ?? 'open'}
                  data-testid="day-row"
                  data-user={r.id ?? 'open'}
                  style={{ minHeight: 16 + lv.n * LEVEL }}
                >
                  <span className="dayname">
                    {r.user ? (
                      <span className="av" aria-hidden="true">
                        {initials(r.label)}
                      </span>
                    ) : null}
                    <span>
                      <b>{r.label}</b>
                      <small>
                        {r.user
                          ? T('{h} t planlagt i dag', { h: num(Math.round(planned * 10) / 10) })
                          : T('{n} å dekke', { n: mine.length })}
                      </small>
                      {mine.length > 0 && (
                        <span className={ds.lists}>
                          {[...mine]
                            .sort((p, q) => span(p).from - span(q).from)
                            .map((s) => actList(s, r.label, mine.length > 1))}
                        </span>
                      )}
                    </span>
                  </span>
                  <div
                    className="daytrack"
                    data-testid="day-track"
                    style={{ backgroundSize: `${perHour}px 100%` }}
                    onDragOver={(e) => {
                      if (editable && e.dataTransfer.types.includes('text/shift-id')) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }
                    }}
                    onDrop={(e) => {
                      const id = Number(e.dataTransfer.getData('text/shift-id'));
                      if (!id) return;
                      e.preventDefault();
                      const x = instantAt(e.clientX);
                      onMove(id, r.id, date, x == null ? undefined : clock(x));
                    }}
                  >
                    {marked && (
                      <span className={ds.band} style={marked} aria-hidden="true" data-testid="day-band" />
                    )}
                    {!mine.length && r.user && (
                      <span className="dnone">
                        {T('Ingen vakt')}
                        {addShift(r)}
                      </span>
                    )}
                    {mine.map((s) => {
                      const live = drag && drag.id === s.id ? drag : null;
                      const sp = span(s);
                      const { before, after } = sp;
                      const from = live ? minOf(Math.max(live.start, day0)) : sp.from;
                      const to = live ? minOf(Math.min(live.end, day1)) : sp.to;
                      const style = {
                        left: pct(from),
                        width: pct(to - from),
                        minWidth: 64,
                        top: 8 + (lv.at.get(s.id) ?? 0) * LEVEL,
                      };
                      return (
                        <div
                          className={`dayslot dshift${before ? ' cont-l' : ''}${after ? ' cont-r' : ''}`}
                          key={s.id}
                          style={style}
                          data-testid="day-slot"
                          data-slot-id={s.id}
                        >
                          {/* the edge on the other day is edited there, or in the panel's times (review F02) */}
                          {editable && !s.punch_in_time && !before ? (
                            <span
                              className="grip left"
                              aria-hidden="true"
                              data-testid={`resize-start-${s.id}`}
                              onPointerDown={beginResize(s, 'start')}
                            />
                          ) : null}
                          {before && (
                            <span className="dcont l" title={T('Fortsetter fra i går')}>
                              <span className="sr">{T('fra i går')}</span>
                            </span>
                          )}
                          {after && (
                            <span className="dcont r" title={T('Fortsetter i morgen')}>
                              <span className="sr">{T('fortsetter i morgen')}</span>
                            </span>
                          )}
                          {entriesFor(s)}
                          {!live && lane(s, r.label)}
                          {/* + on the shift's head adds an activity (V19); kept 14 px in from the end so it
                            never covers the end's resize grip, which V19's did */}
                          {!live &&
                          editable &&
                          (onActivity || onEdit) &&
                          !busy(s.id) &&
                          ((to - from) / 60) * perHour >= 120 ? (
                            <button
                              type="button"
                              className={ds.plus}
                              data-testid={`act-add-${s.id}`}
                              aria-label={T('Legg til aktivitet, {who} {when}', {
                                who: r.label,
                                when: `${clock(sp.a)}–${clock(sp.b)}`,
                              })}
                              title={T('Legg til aktivitet')}
                              onClick={() => addAct(s)}
                            >
                              <Icon name="plus" />
                            </button>
                          ) : null}
                          {editable && !s.punch_in_time && !after ? (
                            <span
                              className="grip right"
                              aria-hidden="true"
                              data-testid={`resize-end-${s.id}`}
                              onPointerDown={beginResize(s, 'end')}
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {drag ? (
        <output data-testid="resize-preview">
          {clock(drag.start)}–{clock(drag.end)}
        </output>
      ) : null}
      {gest?.moved
        ? (() => {
            const s = onDay.find((x) => x.id === gest.sid);
            const over = s && gest.kind !== 'split' ? overlapsOf(s, gest.key, gest.a, gest.b) : [];
            return (
              <output data-testid="act-preview" className={over.length ? 'bad' : undefined}>
                {gest.name} {clock(gest.a)}–{clock(gest.b)}
                {gest.r ? ` · ${gest.r.name} ${clock(gest.b)}–${clock(gest.r.b)}` : ''}
                {over.length
                  ? ` · ${T('Overlapper {what}', { what: over.map((x) => `${x.name} ${clock(x.a)}–${clock(x.b)}`).join(', ') })}`
                  : ''}
              </output>
            );
          })()
        : null}
    </div>
  );
}
